import '../SettingsPage.scss';
import { Button, Dropdown, Icon, Input } from '@design-system/index';
import PasswordCheck from '@components/PasswordCheck/PasswordCheck';
import { ModifierLine, ModifierFull, PoubelleFull, PoubelleLine } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useUsersPageVM } from './useUsersPageVM';
import { Formik } from 'formik';
import { ROLE } from '@utils/enumTranslation';
import { User, UserToCreate } from '@services/core/auth/auth.types';

export default function UsersPage() {
    const { handleDeleteUser, updateUser, createUser, MySwal, needReload, useGetUsersMutation } = useUsersPageVM();
    const columnHelper = createColumnHelper<User>();

    const handleModificationUsers = async (user: User | null) => {
        await MySwal.fire<UserToCreate>({
            title: user ? "Modification d'un utilisateur" : "Création d'un utilisateur",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: user ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            html: (
                <Formik
                    initialValues={{
                        firstname: user?.firstname ?? '',
                        lastname: user?.lastname ?? '',
                        email: user?.email ?? '',
                        role: user?.role ?? 'user',
                        password: ''
                    }}
                    validate={(values) => {
                        const errors: Record<string, string> = {};

                        if (values.password && values.password.length > 0) {
                            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_=+-]).{8,50}$/;
                            if (!passwordRegex.test(values.password)) {
                                errors.password = 'Le mot de passe ne respecte pas les critères requis';
                            }
                        }

                        return errors;
                    }}
                    onSubmit={(values) => {
                        if (user) {
                            updateUser({
                                id: user.id,
                                firstname: values.firstname,
                                lastname: values.lastname,
                                email: values.email,
                                role: values.role,
                                password: values.password
                            });
                        } else {
                            createUser({
                                firstname: values.firstname,
                                lastname: values.lastname,
                                password: values.password,
                                email: values.email,
                                role: values.role
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
                        <form id="form-user" onSubmit={handleSubmit}>
                            <Input
                                name="firstname"
                                label="Prénom"
                                placeholder="Prénom"
                                value={values.firstname}
                                onChange={handleChange}
                                required
                            />
                            <Input name="lastname" label="Nom" placeholder="Nom" value={values.lastname} onChange={handleChange} required />
                            <Input name="email" label="Email" placeholder="Email" value={values.email} onChange={handleChange} required />
                            <Dropdown
                                label="Rôle"
                                required
                                options={Object.entries(ROLE).map(([key, value]) => ({
                                    id: key,
                                    label: value
                                }))}
                                value={values.role}
                                onOptionChange={(option) => setFieldValue('role', option.id)}
                            />
                            <Input
                                name="password"
                                type="password"
                                label={user ? 'Mot de passe (laisser vide pour ne pas le modifier)' : 'Mot de passe'}
                                placeholder="Mot de passe"
                                value={values.password}
                                onChange={handleChange}
                                required={!user}
                            />
                            {errors.password && touched.password && (
                                <div style={{ color: 'red', fontSize: '12px', textAlign: 'left' }}>{errors.password}</div>
                            )}
                            <PasswordCheck password={values.password} />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-user') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const columns = [
        columnHelper.accessor('firstname', {
            header: 'Prénom',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('lastname', {
            header: 'Nom',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('email', {
            header: 'Email',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('role', {
            header: 'Rôle',
            enableSorting: true,
            cell: (info) => {
                return <>{ROLE[info.getValue()]}</>;
            }
        }),
        columnHelper.accessor('id', {
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationUsers(info.row.original);
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<PoubelleFull />} size={20} color="primary" />}
                            icon={<Icon icon={<PoubelleLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleDeleteUser(info.row.original.id.toString());
                            }}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<User>[];

    return (
        <>
            <div className="settings-tabs-content">
                <MutationTable
                    noHeader
                    key={'users'}
                    columns={columns}
                    mutationHook={useGetUsersMutation}
                    defaultSorting={{
                        field: 'firstname',
                        direction: 'desc'
                    }}
                    selectable={false}
                    needReload={needReload}
                    createButtonOnClick={() => handleModificationUsers(null)}
                    createButtonLabel="Créer un utilisateur"
                />
            </div>
        </>
    );
}
